import { useEffect, useState } from 'react'
import { Banner, Button, Checkbox, Field, Input, KeyValueList, Modal, Spinner, StatCard, StatGrid } from '../../../ui'
import { DeepDivePreview, DeepDiveScope, RunnerState, deepDiveApi, messageOf, minutesText, pounds } from '../commsShared/deepDive'
import styles from './styles.module.scss'

interface Props {
    open: boolean
    onClose: () => void
    scope: DeepDiveScope
    parentLabel: string
    scopeLabel: string
    runner: RunnerState
    /** How many threads at this outcome have never been labelled (so the "only new" line can be explained). */
    pending: number
    labelled: number
    onStarted: () => void
}

/**
 * "Deep dive N threads?" — what is about to be sent to the model, roughly what
 * it will cost and how long it will take, and a spend cap the run cannot pass.
 * Nothing runs until Start is pressed; the same modal serves the first big run
 * and the small top-ups as new replies arrive.
 */
export const DeepDiveConfirm = ({ open, onClose, scope, parentLabel, scopeLabel, runner, pending, labelled, onStarted }: Props) => {
    const [onlyNew, setOnlyNew] = useState(true)
    const [preview, setPreview] = useState<DeepDivePreview | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [capPounds, setCapPounds] = useState<string>('')
    const [capTouched, setCapTouched] = useState(false)
    const [starting, setStarting] = useState(false)

    useEffect(() => {
        if (!open) return
        let cancelled = false
        setLoading(true)
        setError(null)
        deepDiveApi
            .preview(scope, onlyNew)
            .then((p) => {
                if (cancelled) return
                setPreview(p)
                if (!capTouched) setCapPounds(String(Math.max(1, Math.ceil(p.suggestedCapPence / 100))))
            })
            .catch((e) => {
                if (!cancelled) setError(messageOf(e, 'Could not build the preview'))
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, onlyNew, scope.parent, scope.mailbox, scope.bucket])

    useEffect(() => {
        if (!open) {
            setOnlyNew(true)
            setPreview(null)
            setCapTouched(false)
            setCapPounds('')
            setError(null)
        }
    }, [open])

    const cap = Number(capPounds)
    const capOk = Number.isFinite(cap) && cap >= 1
    const capBelowLikely = preview && capOk && cap * 100 < preview.costPence.likely
    const canStart = Boolean(preview && preview.threads > 0 && capOk && runner.canRun && !loading && !starting)

    const start = async () => {
        if (!preview || !canStart) return
        setStarting(true)
        setError(null)
        try {
            await deepDiveApi.start(scope, { onlyNew, maxCostPounds: cap, estCostPence: preview.costPence.likely, estMinutes: preview.minutes.likely })
            onStarted()
            onClose()
        } catch (e) {
            setError(messageOf(e, 'The deep dive could not be started'))
        } finally {
            setStarting(false)
        }
    }

    const threads = preview?.threads ?? 0

    return (
        <Modal
            open={open}
            onClose={onClose}
            size="md"
            title={`Deep dive · ${parentLabel}`}
            subtitle={scopeLabel}
            footer={
                <>
                    <Button variant="neutral" onClick={onClose} disabled={starting}>
                        Cancel
                    </Button>
                    <Button onClick={start} disabled={!canStart} loading={starting} title={!runner.canRun ? 'The comms worker is not available' : threads === 0 ? 'Nothing to send' : `Send ${threads.toLocaleString('en-GB')} thread${threads === 1 ? '' : 's'} to the model`}>
                        {threads > 0 ? `Start · ${threads.toLocaleString('en-GB')} thread${threads === 1 ? '' : 's'}` : 'Start'}
                    </Button>
                </>
            }
        >
            <div className={styles.confirmBody}>
                <p className={styles.confirmIntro}>
                    For each thread the model reads the email that set its outcome and returns a sub-category, the details, and the <strong>exact wording from the email</strong> that supports it. Every quote is checked against the email before it is stored. Runs only on the comms worker; nothing is sent until you press Start.
                </p>

                {!runner.canRun && (
                    <Banner tone="warn" title={runner.reason === 'worker_required' ? 'The pipeline runner is not set to Worker' : 'The comms worker cannot be reached'}>
                        {runner.reason === 'worker_required'
                            ? 'The deep dive only runs on the comms worker. On the Pipeline page, under Comms settings, set "Who runs the pipeline" to Worker.'
                            : 'The worker did not answer its health check, so the run cannot start. Check the worker on the Pipeline page and try again.'}
                    </Banner>
                )}

                {error && <Banner tone="bad">{error}</Banner>}

                {loading && !preview ? (
                    <Spinner block text="Counting threads and estimating cost…" />
                ) : preview ? (
                    <>
                        <StatGrid minColumn={150}>
                            <StatCard compact label="Threads to send" value={preview.threads} sub={onlyNew ? `${pending.toLocaleString('en-GB')} never labelled` : 'every thread at this outcome'} />
                            <StatCard compact label="Estimated cost" value={pounds(preview.costPence.likely, { approx: true })} format={false} sub={`${pounds(preview.costPence.low)} – ${pounds(preview.costPence.high)}`} />
                            <StatCard compact label="Estimated time" value={minutesText(preview.minutes.likely)} format={false} sub={`up to ${minutesText(preview.minutes.high)} · ${preview.concurrency} at a time`} />
                        </StatGrid>

                        <KeyValueList
                            columns
                            items={[
                                { label: 'Model', value: preview.models.primary ?? '—' },
                                { label: 'Fallback', value: preview.models.fallback ?? '—' },
                                { label: 'Per thread', value: `~${preview.tokens.input.toLocaleString('en-GB')} tokens in · ~${preview.tokens.output.toLocaleString('en-GB')} out` },
                                { label: 'Email size', value: `~${Math.round(preview.size.avgOutcomeChars).toLocaleString('en-GB')} characters · ${preview.size.avgMessages} emails per thread` },
                                { label: 'Estimate based on', value: preview.basis === 'observed' ? `${preview.observedRuns.toLocaleString('en-GB')} thread${preview.observedRuns === 1 ? '' : 's'} read in earlier runs` : 'token counts (no earlier run yet)' },
                                { label: 'Sub-categories so far', value: preview.subcategoriesSoFar.toLocaleString('en-GB') },
                            ]}
                        />

                        <div className={styles.confirmControls}>
                            <Checkbox
                                label={labelled > 0 ? `Only threads not yet labelled (${pending.toLocaleString('en-GB')} of ${(pending + labelled).toLocaleString('en-GB')})` : 'Only threads not yet labelled'}
                                hint={labelled > 0 ? 'Untick to re-read every thread, including the ones already labelled.' : 'Every thread here is new, so this makes no difference yet.'}
                                checked={onlyNew}
                                onChange={(e) => setOnlyNew(e.target.checked)}
                                disabled={loading || starting}
                            />
                            <Field label="Spend cap (£)" hint={capBelowLikely ? 'Below the likely cost: the run will stop early and can be topped up later.' : 'The run stops when it reaches this figure. Suggested: 1.5× the high estimate.'} error={!capOk ? 'At least £1' : undefined}>
                                <Input
                                    type="number"
                                    min={1}
                                    step={1}
                                    value={capPounds}
                                    invalid={!capOk}
                                    onChange={(e) => {
                                        setCapTouched(true)
                                        setCapPounds(e.target.value)
                                    }}
                                    style={{ maxWidth: 140 }}
                                />
                            </Field>
                        </div>

                        {preview.threads === 0 && <Banner tone="info">Nothing to send: every thread at this outcome{onlyNew ? ' already has a label. Untick "Only threads not yet labelled" to re-read them.' : ' has been read.'}</Banner>}
                    </>
                ) : null}
            </div>
        </Modal>
    )
}
